/* Quick add on product cards (snippets/theme-product-card.liquid), "Pairs well with"
   (sections/theme-product-pairs.liquid) and the empty cart drawer row.
   Submits form[data-mv-quick-add] / form[data-mv-add-many] with fetch('/cart/add.js'),
   asking for the cart drawer section in the same request, then dispatches the stock
   CartLinesUpdateEvent (action 'add'): the header count (cart-icon.js) refreshes, the
   drawer re-renders from the returned section and <cart-drawer-component auto-open>
   opens it. The toast is only the fallback where no drawer is rendered (the /cart page,
   or cart type "page"), and for errors.
   Without JS the form posts to /cart/add as a normal form. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const root = () => window.Shopify?.routes?.root || '/';
let toastTimer = 0;

/** Adds the section ids of every <cart-items-component> (the drawer) to a cart/add.js body. */
function sectionsFor(body) {
  const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el.getAttribute('data-section-id'))
    .filter(Boolean);
  if (!ids.length) return body;
  return { ...body, sections: [...new Set(ids)].join(','), sections_url: window.location.pathname };
}

/* The drawer opens as soon as an add starts when the cart already has lines; on an
   error close it again (unless the add came from inside it) so the toast is visible. */
function closeDrawerFor(form) {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  if (drawer?.isOpen && !drawer.contains(form)) drawer.close();
}

function toast(message, isError) {
  let el = document.getElementById('MvToast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'MvToast';
    el.className = 'mv-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.appendChild(el);
  }
  el.classList.toggle('mv-toast--error', !!isError);
  el.textContent = '';
  const text = document.createElement('span');
  text.className = 'mv-toast__text';
  text.textContent = message;
  el.appendChild(text);
  if (!isError) {
    const link = document.createElement('a');
    link.className = 'mv-toast__link';
    link.href = `${root()}cart`;
    link.setAttribute('data-mv-cart-open', '');
    link.textContent = 'View cart';
    el.appendChild(link);
  }
  el.classList.add('is-open');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-open'), isError ? 6000 : 4000);
}

function setState(button, state) {
  const label = button.querySelector('[data-mv-quick-add-text]');
  button.classList.toggle('is-busy', state === 'busy');
  button.classList.toggle('is-done', state === 'done');
  if (state === 'busy') {
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'Adding…';
  } else {
    button.removeAttribute('aria-busy');
    if (label) label.textContent = state === 'done' ? 'Added' : button.dataset.mvLabel || 'Add to cart';
  }
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-mv-quick-add], [data-mv-add-many]')) return;
  event.preventDefault();
  const button = form.querySelector('[data-mv-quick-add-button]');
  if (!button || button.getAttribute('aria-busy') === 'true') return;

  // [data-mv-add-many] ("Pairs well with"): every [data-mv-add-id] input is one line, quantity 1
  let items;
  if (form.matches('[data-mv-add-many]')) {
    items = [...form.querySelectorAll('[data-mv-add-id]')]
      .map((input) => Number(input.value))
      .filter(Boolean)
      .map((id) => ({ id, quantity: 1 }));
  } else {
    const id = form.querySelector('input[name="id"]')?.value;
    const quantity = parseInt(form.querySelector('input[name="quantity"]')?.value || '1', 10) || 1;
    items = id ? [{ id: Number(id), quantity }] : [];
  }
  if (!items.length) return;
  const quantity = items.reduce((n, item) => n + item.quantity, 0);

  setState(button, 'busy');
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(sectionsFor({ items })),
    });
    const body = await res.json();
    const cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
    const failed = !res.ok || !!body.status;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: failed,
        itemCount: quantity,
        items: cart.items,
        sections: failed ? undefined : body.sections || undefined,
        source: 'theme-quick-add',
      },
    });
    if (failed) {
      form.dispatchEvent(new CartErrorEvent({ error: body.message || 'Add to cart failed', code: 'INVALID' }));
      closeDrawerFor(form);
      setState(button, 'idle');
      toast(`Error: ${body.description || body.message || 'The item could not be added.'}`, true);
      return;
    }
    setState(button, 'done');
    if (!document.getElementById('cart-drawer')) {
      toast(items.length > 1 ? 'Both items were added to your cart.' : 'Added to your cart.', false);
    }
    setTimeout(() => setState(button, 'idle'), 2000);
  } catch (error) {
    deferred.reject(error);
    closeDrawerFor(form);
    setState(button, 'idle');
    toast('Error: The item could not be added. Please try again.', true);
  }
});
